import React, { useState } from 'react';
import { AlternativeSummary } from '../types';
import { uniqueModeEmojis } from '../../../shared/transport';
import { ceilHours, ceilEurosFromCents, euroRangeLabel, formatEuroExact, stayCostStateOf } from '../utils';
import RouteBadge from './RouteBadge';
import MainTag from './MainTag';
import StaleTag from './StaleTag';
import Flags from './Flags';
import KebabMenu, { KebabIcons, KebabItem } from './KebabMenu';
import { hotelNightsSavedLabel, nightRidesLabel } from '../nightTrain';
import { CAN_HOVER } from '../../../shared/canHover';

const pillStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 8px',
  borderRadius: 14,
  background: '#f4f6f3',
  fontSize: 11,
  color: '#16241d',
  // Hug content when it fits, but never exceed the card; in the rare long case
  // (many modes + big numbers) the tokens wrap to a second line inside the pill
  // instead of overflowing the card edge. Tokens themselves stay unbroken.
  maxWidth: '100%',
  flexWrap: 'wrap',
  whiteSpace: 'nowrap',
};

// Selectable card for one journey alternative in the AlternativesSwitcher:
// RouteBadge + optional MainTag + name + quick stats (mode emojis, stops, cost).
// Has a selected state. (design-handoff.md §B.2)

export interface AlternativeJourneyChipProps {
  alt: AlternativeSummary;
  selected: boolean;
  onSelect: (id: string) => void;
  /** Clicking an already-selected chip opens its journey detail page. */
  onOpen: (id: string) => void;
  /** Kebab menu → Delete: remove this alternative (confirmed by the parent).
   *  Optional so read-only surfaces (shared views) can drop the row. */
  onDelete?: (id: string) => void;
  /** Kebab menu → Rename (parent opens the name dialog). */
  onRename?: (id: string) => void;
  /** Kebab menu → Set as main. Hidden when this alternative is already main. */
  onSetMain?: (id: string) => void;
  /** Kebab menu → Create copy (parent opens the name dialog). */
  onCopy?: (id: string) => void;
  /** Label for the copy row (shared views say "Save a copy"). */
  copyLabel?: string;
  /** Kebab menu → Delete other alternatives. Only passed when there IS another
   *  one to delete, so the row hides itself on a single-alternative trip. */
  onDeleteOthers?: (id: string) => void;
  /** Hover preview: fires with the alt id on mouse enter, null on leave. */
  onHoverChange?: (id: string | null) => void;
}

/** What an alternative's ⋮ menu can do. Optional handlers drop their row. */
export interface AlternativeMenuHandlers {
  onOpen: (id: string) => void;
  onRename?: (id: string) => void;
  onCopy?: (id: string) => void;
  /** Label for the copy row (shared views say "Save a copy"). */
  copyLabel?: string;
  onSetMain?: (id: string) => void;
  /** Pass only when there IS another alternative to delete. */
  onDeleteOthers?: (id: string) => void;
  onDelete?: (id: string) => void;
}

/** The alternative's ⋮ menu rows: one list for the chip (phones, tablets) and
 *  the desktop card (RouteFocus `row`), so both offer the same actions. */
export function alternativeMenuItems(alt: AlternativeSummary, h: AlternativeMenuHandlers): KebabItem[] {
  return [
    { label: 'Details', icon: KebabIcons.open, onSelect: () => h.onOpen(alt.id) },
    { label: 'Rename', icon: KebabIcons.pencil, hidden: !h.onRename, onSelect: () => h.onRename?.(alt.id) },
    { label: h.copyLabel ?? 'Create copy', icon: KebabIcons.copy, hidden: !h.onCopy, onSelect: () => h.onCopy?.(alt.id) },
    // Only meaningful on a non-main alternative.
    { label: 'Set as main', icon: KebabIcons.star, hidden: !h.onSetMain || alt.isMain, onSelect: () => h.onSetMain?.(alt.id) },
    { label: 'Delete other alternatives', icon: KebabIcons.trash, tone: 'danger', hidden: !h.onDeleteOthers, onSelect: () => h.onDeleteOthers?.(alt.id) },
    { label: 'Delete', icon: KebabIcons.trash, tone: 'danger', hidden: !h.onDelete, onSelect: () => h.onDelete?.(alt.id) },
  ];
}

function formatCost(cents: number, currency: string): string {
  const symbol = currency === 'EUR' ? '€' : `${currency} `;
  return `${symbol}${ceilEurosFromCents(cents)}*`; // rounded-up estimate
}

const AlternativeJourneyChip: React.FC<AlternativeJourneyChipProps> = ({ alt, selected, onSelect, onOpen, onDelete, onRename, onCopy, copyLabel = 'Create copy', onSetMain, onDeleteOthers, onHoverChange }) => {
  // Two self-describing pills (approx figures, * per ApproxNote):
  //  1. places merged with country flags  →  "📍6 in 🇩🇪🇪🇪"
  //  2. moving time + modes + cost         →  "🕒 20h* by 🚆🚌 for €130–150*"
  //     With night trains (night trains spec §7.6, backlog #53) the moving
  //     time is split: "2 night train rides + 9h* by 🚆🚌 for €130–150*"
  //     (Paulo 2026-09-30: rides, not nights aboard). The rides are timetable
  //     facts (no star); the hours stay an estimate.
  const nights = alt.nightsOnBoard || 0;
  const rides = alt.nightRides || 0;
  const hours = ceilHours(rides > 0 && alt.dayTimeHours != null ? alt.dayTimeHours : alt.movingTimeHours);
  // The RANGE (sum of the legs' price bands), not the midpoint total; the
  // midpoint stays as a fallback for summaries without band data (orphans).
  const cost = alt.costLowCents != null && alt.costHighCents != null
    ? `${euroRangeLabel(alt.costLowCents, alt.costHighCents)}*`
    : alt.costCents != null ? formatCost(alt.costCents, alt.costCurrency) : null;

  // Prices are estimated asynchronously; the trip page polls while any are
  // missing, so the cost lands on the pill by itself — the chip only shows an
  // "Estimating…" status meanwhile (no click-to-load CTA; backlog #21).
  const priceUnknown = cost == null;

  // Desktop hover: preview this alternative on the RouteFocus card AND mark
  // this chip with the same dark-green outline as the selected one.
  const hoverEnabled = CAN_HOVER && !!onHoverChange;
  const [hovered, setHovered] = useState(false);
  const outlined = selected || hovered;

  return (
    <div
      style={{ position: 'relative', flex: '0 0 auto' }}
      onMouseEnter={hoverEnabled ? () => { setHovered(true); onHoverChange!(alt.id); } : undefined}
      onMouseLeave={hoverEnabled ? () => { setHovered(false); onHoverChange!(null); } : undefined}
    >
      <style>{`@keyframes altchip-pulse{0%,100%{opacity:.35}50%{opacity:1}}`}</style>
      <button
      role="tab"
      aria-selected={selected}
      // First click focuses this alternative; clicking the already-focused one
      // opens its journey detail page.
      onClick={() => (selected ? onOpen(alt.id) : onSelect(alt.id))}
      style={{
        flex: '0 0 auto',
        width: 184,
        textAlign: 'left',
        padding: 12,
        borderRadius: 16,
        background: '#ffffff',
        border: `1.5px solid ${outlined ? '#157a55' : 'rgba(20,40,30,0.10)'}`,
        boxShadow: outlined ? '0 2px 10px rgba(21,122,85,0.14)' : 'none',
        cursor: 'pointer',
        scrollSnapAlign: 'start',
      }}
    >
      {/* Reserve top-right space so badges never slide under the kebab. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, paddingRight: 24 }}>
        <RouteBadge index={alt.routeIndex} />
        {alt.isMain && <MainTag />}
        {alt.isStale && <StaleTag compact />}
      </div>
      <div
        style={{
          fontWeight: 600,
          color: '#16241d',
          fontSize: 14,
          marginBottom: 8,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {alt.title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#16241d' }}>
        {/* Stay cost rides line 1 (Paulo 2026-08-18): exact user-entered sum
            (no star), "+" = at least this, some stays unpriced, "?" = none
            priced yet. Omitted entirely when there is no overnight stay. */}
        <span style={pillStyle}>
          <span>📍{alt.stopsCount}{alt.countrySet.length > 0 ? ' in' : ''}</span>
          {alt.countrySet.length > 0 && <Flags countries={alt.countrySet} max={3} />}
          {(() => {
            const s = { sumCents: alt.stayCostCents ?? null, staysPriced: alt.staysPriced ?? 0, staysTotal: alt.staysTotal ?? 0 };
            if (s.staysTotal === 0 && s.sumCents == null) return null;
            const st = stayCostStateOf(s);
            if (st === 'none') return <span>for €?</span>;
            return <span>for {formatEuroExact(s.sumCents!)}{st === 'partial' ? '+' : ''}</span>;
          })()}
          {/* Night trains replace hotel nights: said next to the stay cost,
              only where stays are priced (spec §7.6); with none priced the
              nights aboard on line 2 say it without money. */}
          {nights > 0 && (alt.stayCostCents ?? null) != null && <span>{hotelNightsSavedLabel(nights)}</span>}
        </span>
        {/* Reads as a sentence — "{X}h by {modes} for €{Y}". No clock emoji; on a
            very long route the tokens wrap to a second line inside the pill
            (see pillStyle) rather than overflowing the narrow card. */}
        <span style={pillStyle}>
          {rides > 0 && <span><span aria-hidden>🌙 </span>{nightRidesLabel(rides)}{hours > 0 ? ' +' : ''}</span>}
          {(rides === 0 || hours > 0) && <span>{hours}h*{alt.transportModes.length > 0 ? ' by' : ''}</span>}
          {rides > 0 && hours === 0 && alt.transportModes.length > 0 && <span>by</span>}
          {alt.transportModes.length > 0 && (
            <span style={{ display: 'inline-flex', gap: 2 }}>
              {uniqueModeEmojis(alt.transportModes).slice(0, 4).map((m) => (
                <span key={m.emoji} title={m.label}>{m.emoji}</span>
              ))}
            </span>
          )}
          {cost && <span>for {cost}</span>}
        </span>
        {priceUnknown && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#6c7872' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#157a55', animation: 'altchip-pulse 1.4s infinite' }} />
            Estimating prices…
          </span>
        )}
      </div>
      </button>
      {/* Kebab lives OUTSIDE the button (no nested interactive) and stops
          propagation so opening the menu doesn't select/open the chip. */}
      <div style={{ position: 'absolute', top: 10, right: 10 }} onClick={(e) => e.stopPropagation()}>
        <KebabMenu
          ariaLabel="Alternative options"
          items={alternativeMenuItems(alt, { onOpen, onRename, onCopy, copyLabel, onSetMain, onDeleteOthers, onDelete })}
        />
      </div>
    </div>
  );
};

export default AlternativeJourneyChip;
